import React, { useRef, useEffect } from 'react';
import { Input } from '../ui/input';
import { Alert, AlertDescription } from '../ui/alert';
import { ShoppingCart, X } from 'lucide-react';

/**
 * Painel esquerdo do PDV (55% da tela): header fixo + tabela do carrinho com
 * rolagem interna (apenas as linhas rolam, o cabeçalho permanece visível).
 * O item selecionado via teclado (setas ↑↓) recebe destaque; Delete o remove.
 * 100% controlado pelas props (nenhum estado local).
 */
export const PDVCartList = ({
  company,
  cart,
  updateQuantity,
  cancelItem,
  selectedItemIndex = -1,
  error,
  success,
}) => {
  // Rola automaticamente a lista para o último item lançado (mostra sempre o mais recente)
  const scrollRef = useRef(null);
  const lastActive = [...cart].reverse().find((i) => !i.cancelled);
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [cart.length, lastActive?.product_id, lastActive?.quantity]);
  // Mantém o item selecionado (setas ↑↓) visível na área de rolagem
  useEffect(() => {
    if (selectedItemIndex < 0 || !scrollRef.current) return;
    const row = scrollRef.current.querySelector(`[data-testid="cart-item-${selectedItemIndex}"]`);
    if (row) row.scrollIntoView({ block: 'nearest' });
  }, [selectedItemIndex]);
  return (
    <div className="w-full lg:w-[55%] p-3 sm:p-4 overflow-visible lg:overflow-hidden flex flex-col pdv-main-area order-2 lg:order-none min-h-[42vh] lg:min-h-0" data-testid="pdv-cart-list">
      <div className="flex items-start justify-between mb-3 shrink-0">
        <div className="min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl font-black tracking-tight pdv-text" data-testid="pdv-company-default-name">Smart Flow</h1>
            {company.logo_base64 ? (
              <>
                <span className="w-px h-8 bg-slate-400/40 shrink-0" aria-hidden="true" />
                <img src={company.logo_base64} alt={company.trade_name || 'Logo da loja'} className="h-[52px] w-[52px] rounded-full object-cover shrink-0" data-testid="pdv-company-logo" />
              </>
            ) : (
              company.trade_name && company.trade_name !== 'Smart Flow' && (
                <p className="font-bold text-blue-500 truncate">{company.trade_name}</p>
              )
            )}
          </div>
          <div className="text-xs pdv-text-muted flex flex-wrap gap-x-3">
            {company.cnpj && <span>CNPJ: {company.cnpj}</span>}
            {company.phone && <span>Fone: {company.phone}</span>}
            {company.city && <span>{company.city}{company.state ? ` - ${company.state}` : ''}</span>}
          </div>
        </div>
      </div>

      <div className="pdv-card rounded-md border overflow-hidden flex flex-col flex-1 min-h-0">
        <div className="bg-slate-900 text-white px-4 py-2 shrink-0 hidden lg:block">
          <div className="grid grid-cols-12 gap-2 text-xs uppercase tracking-[0.2em] font-bold">
            <div className="col-span-1">Item</div>
            <div className="col-span-2">Código</div>
            <div className="col-span-3">Produto</div>
            <div className="col-span-1 text-center">Qtd</div>
            <div className="col-span-2 text-right">Preço</div>
            <div className="col-span-2 text-right">Subtotal</div>
            <div className="col-span-1 text-right">Ações</div>
          </div>
        </div>
        {cart.length === 0 ? (
          <div className="p-8 text-center pdv-text-muted flex-1 flex flex-col justify-center">
            <ShoppingCart size={48} className="mx-auto mb-2 opacity-30" />
            <p className="text-base">Nenhum item adicionado</p>
            <p className="text-xs mt-1">
              Escaneie um código ou pressione <kbd className="px-2 py-0.5 bg-slate-100 text-slate-700 rounded">F4</kbd> para buscar
            </p>
          </div>
        ) : (
          <div ref={scrollRef} className="divide-y pdv-divider overflow-y-auto flex-1 min-h-0" data-testid="cart-items-scroll">
            {cart.map((item, idx) => {
              const isSelected = idx === selectedItemIndex && !item.cancelled;
              return (
              <div
                key={`${item.product_id}-${idx}`}
                className={`px-3 lg:px-4 py-2.5 transition-colors ${
                  item.cancelled
                    ? 'bg-red-500/10'
                    : isSelected
                      ? 'bg-blue-600/25 ring-2 ring-inset ring-blue-500'
                      : item.no_stock
                        ? 'bg-amber-400/25 hover:bg-amber-400/30'
                        : 'hover:bg-slate-500/10'
                }`}
                data-testid={`cart-item-${idx}`}
                data-selected={isSelected ? 'true' : 'false'}
              >
                {/* ---- MOBILE: card empilhado ---- */}
                <div className="lg:hidden flex items-start gap-3">
                  <span className="text-sm font-mono pdv-text-muted mt-0.5 w-5 shrink-0">{idx + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className={`text-base font-semibold leading-tight ${item.cancelled ? 'line-through text-red-500' : 'pdv-text'}`}>
                      {item.product_name}
                      {item.cancelled && <span className="ml-2 text-[10px] uppercase tracking-wider bg-red-600 text-white px-2 py-0.5 rounded align-middle">Cancelado</span>}
                      {!item.cancelled && item.no_stock && <span className="ml-2 text-[10px] uppercase tracking-wider bg-amber-500 text-white px-2 py-0.5 rounded align-middle" data-testid={`no-stock-badge-${idx}`}>Sem estoque</span>}
                    </p>
                    <div className="text-xs pdv-text-muted font-mono mt-0.5">{item.code || item.barcode || '—'} · R$ {Number(item.unit_price || 0).toFixed(2)}/{item.unit || 'un'}</div>
                    <div className="flex items-center gap-2 mt-1.5">
                      {item.cancelled ? (
                        <span className="line-through pdv-text-muted text-sm">Qtd {item.quantity}</span>
                      ) : (
                        <>
                          <span className="text-[11px] uppercase tracking-wider pdv-text-muted">Qtd</span>
                          <Input
                            type="number"
                            min="0.01"
                            step="0.01"
                            value={item.quantity}
                            onChange={(e) => updateQuantity(item.product_id, parseFloat(e.target.value))}
                            className="w-20 h-9 text-base font-mono pdv-input"
                          />
                        </>
                      )}
                    </div>
                  </div>
                  <div className="text-right shrink-0 flex flex-col items-end gap-1.5">
                    <span className={`text-lg font-black font-mono ${item.cancelled ? 'line-through pdv-text-muted' : 'text-blue-500'}`}>R$ {Number(item.subtotal || 0).toFixed(2)}</span>
                    {!item.cancelled && (
                      <button
                        onClick={() => cancelItem(item.product_id)}
                        className="text-red-500 hover:text-red-400 flex items-center gap-1 text-xs font-bold px-2 py-1 rounded active:scale-95"
                        data-testid={`cancel-item-${idx}`}
                      >
                        <X size={15} /> Cancelar
                      </button>
                    )}
                  </div>
                </div>

                {/* ---- DESKTOP: grid 12 colunas ---- */}
                <div className="hidden lg:grid grid-cols-12 gap-2 items-center">
                <div className="col-span-1 text-base font-mono pdv-text-muted">{idx + 1}</div>
                <div className={`col-span-2 text-base font-mono min-w-0 truncate ${item.cancelled ? 'line-through pdv-text-muted' : 'pdv-text-secondary'}`} title={item.code || item.barcode || ''}>
                  {item.code || item.barcode || '-'}
                </div>
                <div className={`col-span-3 text-base font-mono min-w-0 break-words ${item.cancelled ? 'line-through text-red-500' : 'pdv-text'}`}>
                  {item.product_name}
                  {item.cancelled && <span className="ml-2 text-xs uppercase tracking-wider bg-red-600 text-white px-2 py-0.5 rounded">Cancelado</span>}
                  {!item.cancelled && item.no_stock && <span className="ml-2 text-[10px] uppercase tracking-wider bg-amber-500 text-white px-2 py-0.5 rounded align-middle" data-testid={`no-stock-badge-desktop-${idx}`}>Sem estoque</span>}
                </div>
                <div className="col-span-1 text-center">
                  {item.cancelled ? (
                    <span className="line-through pdv-text-muted">{item.quantity}</span>
                  ) : (
                    <Input
                      type="number"
                      min="0.01"
                      step="0.01"
                      value={item.quantity}
                      onChange={(e) => updateQuantity(item.product_id, parseFloat(e.target.value))}
                      className="w-20 h-8 text-base font-mono mx-auto pdv-input"
                      data-testid={`cart-qty-desktop-${idx}`}
                    />
                  )}
                </div>
                <div className={`col-span-2 text-right text-base font-mono min-w-0 whitespace-nowrap ${item.cancelled ? 'line-through pdv-text-muted' : 'pdv-text-secondary'}`}>
                  R$ {Number(item.unit_price || 0).toFixed(2)}
                </div>
                <div className="col-span-2 text-right text-base font-mono font-bold min-w-0 whitespace-nowrap">
                  <span className={item.cancelled ? 'line-through pdv-text-muted' : 'text-blue-500'}>
                    R$ {Number(item.subtotal || 0).toFixed(2)}
                  </span>
                </div>
                <div className="col-span-1 flex justify-end min-w-0">
                  {!item.cancelled && (
                    <button
                      onClick={() => cancelItem(item.product_id)}
                      title="Cancelar item (item será registrado no log)"
                      className="text-red-500 hover:text-red-400 hover:bg-red-500/10 p-1 rounded flex items-center gap-1 shrink-0 whitespace-nowrap max-w-full overflow-hidden"
                      data-testid={`cancel-item-desktop-${idx}`}
                    >
                      <X size={16} className="shrink-0" />
                      <span className="text-xs font-bold hidden 2xl:inline">Cancelar</span>
                    </button>
                  )}
                </div>
                </div>
              </div>
              );
            })}
          </div>
        )}
      </div>

      {cart.some(i => !i.cancelled) && (
        <p className="text-[11px] pdv-text-muted mt-2 shrink-0" data-testid="cart-keyboard-hint">
          Use <kbd className="px-1.5 py-0.5 bg-slate-500/20 rounded">↑</kbd> <kbd className="px-1.5 py-0.5 bg-slate-500/20 rounded">↓</kbd> para navegar e <kbd className="px-1.5 py-0.5 bg-red-100 text-red-700 rounded">Delete</kbd> para excluir o item destacado
        </p>
      )}

      {error && (
        <Alert variant="destructive" className="mt-3 shrink-0">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert className="mt-3 border-green-600 bg-green-50 shrink-0">
          <AlertDescription className="text-green-700 font-medium">{success}</AlertDescription>
        </Alert>
      )}
    </div>
  );
};
